"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircle, FileSignature, Download, AlertTriangle } from "lucide-react";
import { SignatureStatus } from "@/components/deals/signature/SignatureStatus";
import { SignButton } from "@/components/deals/signature/SignButton";
import type { DealSignatures } from "@/lib/deals/signature";
import { dealDocumentUrl } from "@/lib/deals/document-download/refs";
interface SigningStepProps {
  dealId: string;
  role: "seller" | "buyer";
  signatures: DealSignatures | null;
  onSign: () => Promise<string | void>;
  disabled?: boolean;
  className?: string;
}

/**
 * SigningStep - Complete signing step component
 */
export function SigningStep(t0) {
  const $ = _c(35);
  const {
    dealId,
    role,
    signatures,
    onSign,
    disabled: t1,
    className: t2
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const className = t2 === undefined ? "" : t2;
  const isSeller = role === "seller";
  const myStatus = isSeller ? signatures?.seller_signature_status : signatures?.buyer_signature_status;
  const otherStatus = isSeller ? signatures?.buyer_signature_status : signatures?.seller_signature_status;
  const bothSigned = signatures?.seller_signature_status === "signed" && signatures?.buyer_signature_status === "signed";
  const hasError = !!signatures?.docusign_error_message;
  const t3 = `space-y-6 ${className}`;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center gap-3"><div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"><FileSignature className="w-6 h-6 text-primary" /></div><div><h3 className="font-semibold text-zinc-900">Kaufvertrag unterschreiben</h3><p className="text-sm text-zinc-500">Beide Parteien müssen den Vertrag digital unterschreiben</p></div></div>;
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  let t5;
  if ($[1] !== hasError || $[2] !== signatures?.docusign_error_message) {
    t5 = hasError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg"><div className="flex items-start gap-3"><AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" /><div><p className="font-medium text-red-700">Fehler bei der Unterschrift</p><p className="text-sm text-red-600 mt-1">{signatures?.docusign_error_message}</p><p className="text-sm text-red-600 mt-2">Bitte kontaktieren Sie unser Team für Unterstützung.</p></div></div></div>;
    $[1] = hasError;
    $[2] = signatures?.docusign_error_message;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = signatures?.seller_signature_status ?? null;
  const t7 = signatures?.seller_signed_at;
  let t8;
  if ($[4] !== t6 || $[5] !== t7) {
    t8 = <SignatureStatus role="seller" status={t6} signedAt={t7} />;
    $[4] = t6;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const t9 = signatures?.buyer_signature_status ?? null;
  const t10 = signatures?.buyer_signed_at;
  let t11;
  if ($[7] !== t10 || $[8] !== t9) {
    t11 = <SignatureStatus role="buyer" status={t9} signedAt={t10} />;
    $[7] = t10;
    $[8] = t9;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] !== t11 || $[11] !== t8) {
    t12 = <div className="grid gap-4 md:grid-cols-2">{t8}{t11}</div>;
    $[10] = t11;
    $[11] = t8;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== bothSigned || $[14] !== dealId || $[15] !== disabled || $[16] !== hasError || $[17] !== myStatus || $[18] !== onSign) {
    t13 = !bothSigned && !hasError && <div className="pt-4 border-t border-zinc-200"><SignButton dealId={dealId} status={myStatus ?? null} onSign={onSign} disabled={disabled} /></div>;
    $[13] = bothSigned;
    $[14] = dealId;
    $[15] = disabled;
    $[16] = hasError;
    $[17] = myStatus;
    $[18] = onSign;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== bothSigned || $[21] !== dealId || $[22] !== signatures?.signed_contract_url) {
    t14 = bothSigned && <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg"><div className="flex items-start gap-3"><CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0" /><div><p className="font-medium text-emerald-700">Vertrag vollständig unterschrieben</p><p className="text-sm text-emerald-600 mt-1">Beide Parteien haben den Kaufvertrag unterschrieben. Der Vertrag wird jetzt zur Genehmigung an die UNB weitergeleitet.</p>{signatures?.signed_contract_url && <a href={dealDocumentUrl(dealId, "signed-contract", {
            download: true
          })} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 text-sm font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/15 transition-colors"><Download className="w-4 h-4" />Unterschriebenen Vertrag herunterladen</a>}</div></div></div>;
    $[20] = bothSigned;
    $[21] = dealId;
    $[22] = signatures?.signed_contract_url;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== hasError || $[25] !== myStatus || $[26] !== otherStatus) {
    t15 = myStatus === "signed" && otherStatus !== "signed" && !hasError && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg"><p className="text-sm text-blue-700"><strong>Hinweis:</strong> Sie haben bereits unterschrieben. Warten auf die Unterschrift der anderen Partei.</p></div>;
    $[24] = hasError;
    $[25] = myStatus;
    $[26] = otherStatus;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t12 || $[29] !== t13 || $[30] !== t14 || $[31] !== t15 || $[32] !== t3 || $[33] !== t5) {
    t16 = <div className={t3}>{t4}{t5}{t12}{t13}{t14}{t15}</div>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
    $[32] = t3;
    $[33] = t5;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  return t16;
}